// src/components/Admin/AffiliateApproval.js
import React, { useState, useEffect, useCallback } from 'react';

const AffiliateApproval = () => {
  const [affiliatePrograms, setAffiliatePrograms] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'rejected', 'active'
  const [sortBy, setSortBy] = useState('newest');
  const [vendorFilter, setVendorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newAffiliate, setNewAffiliate] = useState({
    vendorName: '',
    vendorDomain: '',
    contactEmail: '',
    proposedCommission: '',
    category: '',
    notes: '',
    priority: 'medium'
  });

  const categories = ['Fashion', 'Home & Garden', 'Electronics', 'Art & Design', 'Books', 'Furniture', 'Lighting', 'Other'];
  const priorities = ['low', 'medium', 'high', 'urgent'];

  // Mock affiliate program data
  const mockAffiliatePrograms = [
    {
      id: 'aff_001',
      vendorName: 'Made Trade',
      vendorDomain: 'madetradesupply.com',
      contactEmail: 'partnerships@madetrade.com',
      category: 'Home & Garden',
      status: 'pending',
      requestedBy: 'admin',
      requestedAt: Date.now() - 172800000, // 2 days ago
      proposedCommission: 8.0,
      priority: 'high',
      notes: 'Sustainable home goods brand, good fit for our aesthetic',
      pendingProducts: 3,
      estimatedRevenue: 450,
      outreachAttempts: 1,
      lastContact: Date.now() - 86400000, // 1 day ago
      responseStatus: 'pending',
      integrationComplexity: 'medium'
    },
    {
      id: 'aff_002',
      vendorName: 'Schoolhouse Electric',
      vendorDomain: 'schoolhouseelectric.com',
      contactEmail: 'affiliate@schoolhouseelectric.com',
      category: 'Lighting',
      status: 'approved',
      requestedBy: 'admin',
      requestedAt: Date.now() - 604800000, // 7 days ago
      approvedAt: Date.now() - 259200000, // 3 days ago
      approvedBy: 'admin',
      finalCommission: 6.5,
      proposedCommission: 7.0,
      priority: 'medium',
      notes: 'Lighting and home goods, commission negotiated down from 7%',
      pendingProducts: 0,
      activeProducts: 12,
      estimatedRevenue: 890,
      actualRevenue: 234.50,
      outreachAttempts: 2,
      lastContact: Date.now() - 259200000,
      responseStatus: 'approved',
      integrationComplexity: 'low',
      integrationStatus: 'complete',
      programType: 'direct',
      paymentTerms: 'Net 30'
    },
    {
      id: 'aff_003',
      vendorName: 'Small Ceramics Studio',
      vendorDomain: 'smallceramics.shop',
      contactEmail: 'hello@smallceramics.shop',
      category: 'Art & Design',
      status: 'rejected',
      requestedBy: 'user-generated',
      requestedAt: Date.now() - 432000000, // 5 days ago
      rejectedAt: Date.now() - 345600000, // 4 days ago
      rejectedBy: 'admin',
      proposedCommission: 10.0,
      priority: 'low',
      notes: 'Small vendor, no formal affiliate program structure',
      pendingProducts: 1,
      estimatedRevenue: 85,
      outreachAttempts: 2,
      lastContact: Date.now() - 345600000,
      responseStatus: 'no-program',
      rejectionReason: 'Vendor does not have infrastructure for affiliate programs',
      integrationComplexity: 'high'
    },
    {
      id: 'aff_004',
      vendorName: 'CB2',
      vendorDomain: 'cb2.com',
      contactEmail: 'partnerships@cb2.com',
      category: 'Furniture',
      status: 'active',
      requestedBy: 'admin',
      requestedAt: Date.now() - 1209600000, // 14 days ago
      approvedAt: Date.now() - 864000000, // 10 days ago
      approvedBy: 'admin',
      finalCommission: 4.0,
      proposedCommission: 5.0,
      priority: 'high',
      notes: 'Major furniture retailer, lower commission but high volume',
      pendingProducts: 0,
      activeProducts: 45,
      estimatedRevenue: 1200,
      actualRevenue: 1456.78,
      outreachAttempts: 1,
      lastContact: Date.now() - 864000000,
      responseStatus: 'approved',
      integrationComplexity: 'medium',
      integrationStatus: 'complete',
      programType: 'network',
      paymentTerms: 'Net 45',
      networkPartner: 'ShareASale'
    }
  ];

  // Load affiliate programs
  const loadAffiliatePrograms = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setAffiliatePrograms(mockAffiliatePrograms);
      setMessage(`✅ Loaded ${mockAffiliatePrograms.length} affiliate programs`);
    } catch (error) {
      console.error('Error loading affiliate programs:', error);
      setMessage('❌ Error loading affiliate program data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAffiliatePrograms();
  }, [loadAffiliatePrograms]);

  // Filter and sort programs
  const getFilteredPrograms = useCallback(() => {
    let filtered = [...affiliatePrograms];
    
    // Filter by tab
    switch (activeTab) {
      case 'pending':
        filtered = filtered.filter(item => item.status === 'pending');
        break;
      case 'approved':
        filtered = filtered.filter(item => item.status === 'approved');
        break;
      case 'rejected':
        filtered = filtered.filter(item => item.status === 'rejected');
        break;
      case 'active':
        filtered = filtered.filter(item => item.status === 'active');
        break;
    }
    
    // Apply filters
    if (vendorFilter) {
      filtered = filtered.filter(item => 
        item.vendorName.toLowerCase().includes(vendorFilter.toLowerCase()) ||
        item.vendorDomain.toLowerCase().includes(vendorFilter.toLowerCase())
      );
    }
    
    if (categoryFilter) {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.requestedAt || 0) - (a.requestedAt || 0);
        case 'oldest':
          return (a.requestedAt || 0) - (b.requestedAt || 0);
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        case 'commission':
          return (b.proposedCommission || 0) - (a.proposedCommission || 0);
        case 'revenue':
          return (b.estimatedRevenue || 0) - (a.estimatedRevenue || 0);
        case 'vendor':
          return (a.vendorName || '').localeCompare(b.vendorName || '');
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [affiliatePrograms, activeTab, vendorFilter, categoryFilter, sortBy]);

  const filteredPrograms = getFilteredPrograms();

  // Handle affiliate actions
  const handleApproveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Approve ${itemIds.length} affiliate programs?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Approving affiliate programs:', itemIds);
      setMessage(`✅ Would approve ${itemIds.length} affiliate programs (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error approving affiliate programs:', error);
      setMessage('❌ Error approving affiliate programs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectItems = async (itemIds, reason = '') => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    const rejectionReason = reason || prompt('Reason for rejection:');
    if (rejectionReason === null) return;

    if (!window.confirm(`Reject ${itemIds.length} affiliate programs?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Rejecting affiliate programs:', itemIds, 'Reason:', rejectionReason);
      setMessage(`✅ Would reject ${itemIds.length} affiliate programs (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error rejecting affiliate programs:', error);
      setMessage('❌ Error rejecting affiliate programs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactVendor = async (programId) => {
    setIsLoading(true);
    try {
      console.log('Contacting vendor for program:', programId);
      setMessage('✅ Would send vendor contact email (backend API needed)');
    } catch (error) {
      console.error('Error contacting vendor:', error);
      setMessage('❌ Error contacting vendor');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredPrograms.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredPrograms.map(item => item.id)));
    }
  };

  // Handle new affiliate form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAffiliate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitNewAffiliate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const affiliate = {
        ...newAffiliate,
        id: `aff_${Date.now()}`,
        status: 'pending',
        requestedBy: 'admin',
        requestedAt: Date.now(),
        proposedCommission: parseFloat(newAffiliate.proposedCommission)
      };
      
      console.log('Creating new affiliate program request:', affiliate);
      setMessage('✅ Affiliate program request created successfully (backend API needed)');
      setShowCreateForm(false);
      setNewAffiliate({
        vendorName: '',
        vendorDomain: '',
        contactEmail: '',
        proposedCommission: '',
        category: '',
        notes: '',
        priority: 'medium'
      });
      
    } catch (error) {
      console.error('Error creating affiliate program:', error);
      setMessage('❌ Error creating affiliate program');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'active': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'active': return '🟢';
      default: return '❓';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#6c757d';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Affiliate Program Management</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Manage vendor affiliate programs and partnerships
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => window.location.href = '/admin/user-products'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🛍️ User Products
          </button>
          
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Admin Dashboard
          </button>
          
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: showCreateForm ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showCreateForm ? '❌ Cancel' : '🔗 New Affiliate Request'}
          </button>
          
          <button
            onClick={loadAffiliatePrograms}
            disabled={isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(136px, 1fr)))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>
            {affiliatePrograms.filter(p => p.status === 'pending').length}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Pending Approval</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#cce5ff',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004085' }}>
            {affiliatePrograms.filter(p => p.status === 'active').length}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Active Programs</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#d4edda',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>
            {affiliatePrograms.filter(p => p.activeProducts).reduce((sum, p) => sum + (p.activeProducts || 0), 0)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Active Products</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>
            ${affiliatePrograms.filter(p => p.actualRevenue).reduce((sum, p) => sum + (p.actualRevenue || 0), 0).toFixed(2)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Monthly Revenue</div>
        </div>
      </div>

      {/* Create Affiliate Form */}
      {showCreateForm && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>Request New Affiliate Program</h3>
          
          <form onSubmit={handleSubmitNewAffiliate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Vendor Name *
                </label>
                <input
                  type="text"
                  name="vendorName"
                  value={newAffiliate.vendorName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Vendor Domain *
                </label>
                <input
                  type="text"
                  name="vendorDomain"
                  value={newAffiliate.vendorDomain}
                  onChange={handleInputChange}
                  required
                  placeholder="example.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Contact Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={newAffiliate.contactEmail}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Proposed Commission (%)
                </label>
                <input
                  type="number"
                  name="proposedCommission"
                  value={newAffiliate.proposedCommission}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  max="50"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Priority
                </label>
                <select
                  name="priority"
                  value={newAffiliate.priority}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  {priorities.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Category
              </label>
              <select
                name="category"
                value={newAffiliate.category}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Notes
              </label>
              <textarea
                name="notes"
                value={newAffiliate.notes}
                onChange={handleInputChange}
                rows="3"
                placeholder="Reason for request, vendor details, etc."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: isLoading ? '#ccc' : '#0d47a1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Creating...' : 'Submit Affiliate Request'}
            </button>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem'
      }}>
        {[
          { id: 'pending', label: 'Pending Review', count: affiliatePrograms.filter(p => p.status === 'pending').length },
          { id: 'approved', label: 'Approved', count: affiliatePrograms.filter(p => p.status === 'approved').length },
          { id: 'active', label: 'Active Programs', count: affiliatePrograms.filter(p => p.status === 'active').length },
          { id: 'rejected', label: 'Rejected', count: affiliatePrograms.filter(p => p.status === 'rejected').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === tab.id ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Vendor Filter
          </label>
          <input
            type="text"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            placeholder="Vendor name or domain"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Category
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="priority">Priority</option>
            <option value="commission">Commission Rate</option>
            <option value="revenue">Est. Revenue</option>
            <option value="vendor">Vendor A-Z</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {activeTab === 'pending' && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '6px'
        }}>
          <div>
            <button
              onClick={handleSelectAll}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '1rem'
              }}
            >
              {selectedItems.size === filteredPrograms.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              {selectedItems.size} of {filteredPrograms.length} selected
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handleApproveItems(Array.from(selectedItems))}
              disabled={selectedItems.size === 0 || isLoading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem'
              }}
            >
              ✅ Approve Selected ({selectedItems.size})
            </button>
            
            <button
              onClick={() => handleRejectItems(Array.from(selectedItems))}
              disabled={selectedItems.size === 0 || isLoading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem'
              }}
            >
              ❌ Reject Selected ({selectedItems.size})
            </button>
          </div>
        </div>
      )}

      {/* Affiliate Programs Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: '#666' }}>
          Loading affiliate programs...
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {filteredPrograms.map((program) => (
              <div key={program.id} style={{
                border: program.priority === 'urgent' ? '2px solid #dc3545' : '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: program.priority === 'urgent' ? '#fff5f5' : 'white',
                position: 'relative',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {/* Selection checkbox for pending */}
                {activeTab === 'pending' && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(program.id)}
                    onChange={() => handleItemSelect(program.id)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      transform: 'scale(1.2)',
                      zIndex: 1
                    }}
                  />
                )}

                {/* Status and Priority indicators */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1rem',
                  marginTop: activeTab === 'pending' ? '2rem' : '0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getStatusIcon(program.status)}
                    </span>
                    <span style={{
                      backgroundColor: getStatusColor(program.status),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {program.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span style={{
                      backgroundColor: getPriorityColor(program.priority),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {program.priority.toUpperCase()}
                    </span>
                    
                    {program.finalCommission && (
                      <span style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        {program.finalCommission}%
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Program details */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ 
                      margin: 0, 
                      fontSize: '1.1rem', 
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {program.vendorName}
                    </h4>
                    
                    <span style={{
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {program.category}
                    </span>
                  </div>
                  
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                    <strong>Domain:</strong> {program.vendorDomain}
                  </div>
                  
                  {program.contactEmail && (
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                      <strong>Contact:</strong> {program.contactEmail}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                    <strong>Commission:</strong> {program.proposedCommission}%
                    {program.finalCommission && program.finalCommission !== program.proposedCommission && (
                      <span style={{ color: '#28a745' }}> → {program.finalCommission}%</span>
                    )}
                  </div>
                  
                  {program.notes && (
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                      "{program.notes}"
                    </div>
                  )}
                  
                  {/* Product and revenue info */}
                  <div style={{ 
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: program.status === 'active' ? '#e8f5e8' : 
                                   program.status === 'approved' ? '#e3f2fd' :
                                   program.status === 'rejected' ? '#fee' : '#fff3cd',
                    borderRadius: '6px',
                    fontSize: '0.85rem'
                  }}>
                    {program.status === 'active' && (
                      <>
                        <div><strong>Active Products:</strong> {program.activeProducts || 0}</div>
                        <div><strong>Monthly Revenue:</strong> ${(program.actualRevenue || 0).toFixed(2)}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                          Integration: {program.integrationStatus || 'pending'} • 
                          Type: {program.programType || 'direct'} • 
                          Terms: {program.paymentTerms || 'Net 30'}
                        </div>
                      </>
                    )}
                    
                    {program.status === 'approved' && (
                      <>
                        <div><strong>Approved:</strong> {new Date(program.approvedAt).toLocaleDateString()}</div>
                        <div><strong>Final Commission:</strong> {program.finalCommission}%</div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                          Integration complexity: {program.integrationComplexity}
                        </div>
                      </>
                    )}
                    
                    {(program.status === 'pending' || program.status === 'rejected') && (
                      <>
                        <div><strong>Pending Products:</strong> {program.pendingProducts || 0}</div>
                        <div><strong>Est. Revenue:</strong> ${(program.estimatedRevenue || 0).toFixed(2)}/month</div>
                        <div><strong>Outreach Attempts:</strong> {program.outreachAttempts || 0}</div>
                        {program.lastContact && (
                          <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                            Last contact: {new Date(program.lastContact).toLocaleDateString()}
                          </div>
                        )}
                      </>
                    )}
                    
                    {program.status === 'rejected' && program.rejectionReason && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px' }}>
                        <strong style={{ color: '#dc3545' }}>Rejection Reason:</strong>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                          {program.rejectionReason}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.75rem' }}>
                    Requested: {new Date(program.requestedAt).toLocaleDateString()} by {program.requestedBy}
                    {program.approvedAt && (
                      <span> • Approved: {new Date(program.approvedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {program.vendorDomain && (
                    <button
                      onClick={() => window.open(`https://${program.vendorDomain}`, '_blank')}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🔗 Visit Site
                    </button>
                  )}
                  
                  {activeTab === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApproveItems([program.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ✅ Approve
                      </button>
                      
                      <button
                        onClick={() => handleRejectItems([program.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ❌ Reject
                      </button>
                    </>
                  )}
                  
                  {(program.status === 'pending' || program.status === 'approved') && program.contactEmail && (
                    <button
                      onClick={() => handleContactVendor(program.id)}
                      disabled={isLoading}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📧 Contact
                    </button>
                  )}
                  
                  {program.contactEmail && (
                    <button
                      onClick={() => window.open(`mailto:${program.contactEmail}`, '_blank')}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📧
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {filteredPrograms.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>
                {activeTab === 'pending' && 'No pending affiliate programs'}
                {activeTab === 'approved' && 'No approved programs'}
                {activeTab === 'active' && 'No active programs'}
                {activeTab === 'rejected' && 'No rejected programs'}
              </h3>
              <p style={{ margin: 0 }}>
                {activeTab === 'pending' 
                  ? 'No affiliate programs awaiting review' 
                  : `No ${activeTab} affiliate programs yet`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}

      {/* Note about backend API needed */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#856404'
      }}>
        <strong>Note:</strong> This interface shows the affiliate program management workflow. You'll need a backend API to actually manage program approval, vendor outreach, integration setup, and revenue tracking. The approve/reject/contact actions currently just log what would happen.
      </div>
    </div>
  );
};

export default AffiliateApproval;